import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getYear, subYears } from 'date-fns';

import { CustomerApi } from '_api';
import { STORAGE_KEYS } from '_constants/storage';
import { handleError, handleResponse } from '_helpers/api';
import { restoreSessionValue, storeSessionValue } from '_helpers/storage';
import { getPersonalTaxFilingProduct, parseTaxReports } from '_helpers/taxFilingInfo';
import { getOrders } from '_store/orders/slice';
import { refreshForm } from '_store/taxFilingInfo/slice';

export const getOrder = createAsyncThunk(
  'order/getOrder',
  async function ({ customerId, orderId }, { dispatch, rejectWithValue }) {
    try {
      const response = await CustomerApi.getOrder({ customerId, orderId });
      const data = handleResponse(response);

      if (data?.parentOrderUid) {
        const parentResponse = await CustomerApi.getOrder({ customerId, orderId: data?.parentOrderUid });
        const parentData = handleResponse(parentResponse);
        data.parent = parentData;
      }

      const personalTaxFilingProduct = getPersonalTaxFilingProduct(data);
      const { reports } = parseTaxReports(personalTaxFilingProduct?.taxReports);
      const prevYearTag = getYear(subYears(new Date(), 1));
      const prevYearReport = reports[prevYearTag] || {};
      dispatch(refreshForm(prevYearReport));
      return data;
    } catch (error) {
      const processedError = handleError(error);

      if (processedError?.code === 400) {
        dispatch(refreshActiveOrderId(null));
        dispatch(getOrders({ customerId }));
      }
      return rejectWithValue(processedError);
    }
  }
);

const orderSlice = createSlice({
  name: 'order',
  initialState: {
    activeOrderId: null,
    data: {},
    error: null,
    loading: false,
  },
  reducers: {
    refreshActiveOrderId(state, action) {
      storeSessionValue(STORAGE_KEYS.activeOrderId, action.payload);
      state.activeOrderId = action.payload;
    },
    resetActiveOrderId(state) {
      state.activeOrderId = null;
    },
    restoreActiveOrderId(state) {
      const restoredActiveOrderId = restoreSessionValue(STORAGE_KEYS.activeOrderId);

      if (restoredActiveOrderId) {
        state.activeOrderId = restoredActiveOrderId;
      }
    },
    resetData(state) {
      state.data = {};
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrder.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(getOrder.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const { refreshActiveOrderId, resetActiveOrderId, restoreActiveOrderId, resetData, resetError } =
  orderSlice.actions;

export default orderSlice.reducer;
